import { EditableProTable, ProColumns, nanoid } from '@ant-design/pro-components';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker, Form, Tooltip } from 'antd';
import { customTableClassName } from 'base/ui/components/CustomTable';
import CustomSelect from 'base/ui/components/CustomSelect';
import CustomInput from 'base/ui/components/CustomInput';
import * as Icon from 'base/ui/components/Icons';
import { customFilterOption } from 'utils/searchHelper';
import dayjs from 'dayjs';

export interface WasteHandoverItem {
  stt?: number;
  wasteHandoverItemId: string;
  deliveryScheduleTime?: any;
  wasteCode?: string;
  wasteName?: string;
  vendorId?: string;
  storageLocation?: string;
  deliveryNumber?: string;
  note?: string;
}

type WasteHandoverTableProps = {
  id: string;
  editableKeys: React.Key[];
  setEditableRowKeys: (value: React.Key[]) => void;
  wasteOptions: { label: string; value: string }[];
  vendorOptions: { label: string; value: string }[];
  storageOptions: { label: string; value: string }[];

  // dùng để làm phần copy khi có id
  dataDetail?: any;
};

const WasteHandoverTable: React.FC<WasteHandoverTableProps> = (props) => {
  const { id, setEditableRowKeys, wasteOptions, vendorOptions, storageOptions, dataDetail } = props;
  const { t } = useTranslation();
  const form = Form.useFormInstance();

  const formValues = Form.useWatch('wasteHandoverItems', form);
  const safeFormValues: WasteHandoverItem[] = useMemo(
    () => (Array.isArray(formValues) ? formValues.filter(Boolean) : []),
    [formValues]
  );

  useEffect(() => {
    if (dataDetail) {
      // console.log('findVendorId1', vendorOptions);
      // console.log('findVendorId2', dataDetail);
      const items = (Array.isArray(dataDetail) ? dataDetail : [dataDetail]).map((item, index) => {
        const nameFromItem =
          (item as any).productName ||
          (item as any).materialName ||
          '';

        return {
          ...item,
          stt: (item as any).stt ?? index + 1,
          wasteCode: item?.productCode, // Gán wasteCode = productCode
          // Ưu tiên tên hiển thị (materialName) nếu productName trống
          wasteName: nameFromItem,
          deliveryScheduleTime: item?.deliveryDate ? dayjs(item?.deliveryDate) : undefined,
          deliveryNumber: item?.handoverDocumentNumber,
          vendorId: item?.vendorNumber,
          wasteHandoverItemId: nanoid(),
          note: item?.note,
        };
      });
      form.setFieldsValue({
        wasteHandoverItems: items,
      });
      setEditableRowKeys(items.map((item) => item.wasteHandoverItemId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataDetail, form]);

  // Chỉ chuẩn hóa khi số lượng dòng thay đổi (thêm/xóa), tránh nháy khi nhập field.
  useEffect(() => {
    const currentItems = (form.getFieldValue('wasteHandoverItems') || []) as WasteHandoverItem[];
    if (!Array.isArray(currentItems) || currentItems.length === 0) return;

    const normalizedItems = currentItems.map((item, index) => ({
      ...item,
      wasteHandoverItemId: item?.wasteHandoverItemId || nanoid(),
      stt: index + 1,
    }));

    const needPatch =
      normalizedItems.length !== currentItems.length ||
      normalizedItems.some((item, index) => {
        const current = currentItems[index] as WasteHandoverItem | undefined;
        return (
          !current ||
          current.wasteHandoverItemId !== item.wasteHandoverItemId ||
          current.stt !== item.stt
        );
      });

    if (needPatch) {
      form.setFieldValue('wasteHandoverItems', normalizedItems);
    }
  }, [form, safeFormValues.length]);

  // Dùng dữ liệu hiện tại của form làm nguồn duy nhất để tránh lệch state.
  const dataSource: WasteHandoverItem[] = safeFormValues.length > 0
    ? safeFormValues
    : [{ wasteHandoverItemId: id, stt: 1 }];
  const computedEditableKeys: React.Key[] = dataSource
    .map((item) => item?.wasteHandoverItemId)
    .filter(Boolean) as React.Key[];

  const defaultDataTable: ProColumns<WasteHandoverItem>[] = [
    {
      title: t('waste-handover-plan.stt'),
      dataIndex: 'stt',
      key: 'stt',
      fixed: 'left',
      width: 60,
      align: 'center',
      readonly: true,
      render: (_, record, index) => record.stt ?? (typeof index === 'number' ? index + 1 : '-'),
    },
    {
      title: (
        <>
          {t('waste-handover-plan.delivery-schedule-time')}{' '}
          <Tooltip title={t('message.required')}>
            <span style={{ color: '#ff4d4f', cursor: 'help' }}>(*)</span>
          </Tooltip>
        </>
      ),
      dataIndex: 'deliveryScheduleTime',
      key: 'deliveryScheduleTime',
      width: 190,
      valueType: 'date',
      renderFormItem() {
        return (
          <DatePicker
            showTime
            format="DD/MM/YYYY HH:mm"
            placeholder={t('place-holder.select-datetime')}
            style={{ width: '100%' }}
            size="small"
          />
        );
      },
      formItemProps: () => ({
        rules: [
          {
            required: true,
            message: t('waste-handover-plan.delivery-schedule-time-required'),
          },
        ],
        style: { marginBottom: 0 },
      }),
    },
    {
      title: (
        <>
          {t('waste-handover-plan.waste')}{' '}
          <Tooltip title={t('message.required')}>
            <span style={{ color: '#ff4d4f', cursor: 'help' }}>(*)</span>
          </Tooltip>
        </>
      ),
      dataIndex: 'wasteCode',
      key: 'wasteCode',
      width: 250,
      renderFormItem(schema, config, form) {
        return (
          <CustomSelect
            options={wasteOptions}
            allowClear
            showSearch
            optionFilterProp="label"
            filterOption={customFilterOption}
            placeholder={t('place-holder.select')}
            size="small"
            onChange={(value, option: any) => {
              if (option) {
                form.setFieldValue(
                  ['wasteHandoverItems', `${schema.index}`, 'wasteName'],
                  option.label
                );
              } else {
                form.setFieldValue(
                  ['wasteHandoverItems', `${schema.index}`, 'wasteName'],
                  undefined
                );
              }
            }}
          />
        );
      },
      formItemProps: () => ({
        rules: [{ required: true, message: t('waste-handover-plan.waste-required') }],
        style: { marginTop: 0, marginBottom: 0 },
      }),
    },
    {
      title: (
        <>
          {t('waste-handover-plan.vendor')}{' '}
          <Tooltip title={t('message.required')}>
            <span style={{ color: '#ff4d4f', cursor: 'help' }}>(*)</span>
          </Tooltip>
        </>
      ),
      dataIndex: 'vendorId',
      key: 'vendorId',
      width: 350,
      className: 'min-w-[350px]',
      renderFormItem() {
        return (
          <CustomSelect
            options={vendorOptions}
            allowClear
            showSearch
            placeholder={t('place-holder.select')}
            size="small"
            filterOption={(input, option) => {
              const searchText = input.toLowerCase();
              const label = (option?.label as string)?.toLowerCase() || '';
              const value = (option?.value as string)?.toLowerCase() || '';
              return label.includes(searchText) || value.includes(searchText);
            }}
          />
        );
      },
      formItemProps: () => ({
        rules: [{ required: true, message: t('waste-handover-plan.vendor-required') }],
        style: { marginTop: 0, marginBottom: 0 },
      }),
    },
    {
      title: (
        <>
          {t('waste-handover-plan.storage-location')}{' '}
          <Tooltip title={t('message.required')}>
            <span style={{ color: '#ff4d4f', cursor: 'help' }}>(*)</span>
          </Tooltip>
        </>
      ),
      dataIndex: 'storageLocation',
      key: 'storageLocation',
      width: 220,
      renderFormItem() {
        return (
          <CustomSelect
            options={storageOptions}
            allowClear
            showSearch
            optionFilterProp="label"
            filterOption={customFilterOption}
            placeholder={t('place-holder.select')}
            size="small"
          />
        );
      },
      formItemProps: () => ({
        rules: [
          {
            required: true,
            message: t('waste-handover-plan.storage-location-required'),
          },
        ],
        style: { marginTop: 0, marginBottom: 0 },
      }),
    },
    // số biên bản bàn giao
    {
      title: t('waste-handover-plan.delivery-number'),
      dataIndex: 'deliveryNumber',
      key: 'deliveryNumber',
      width: 150,
      renderFormItem() {
        return (
          <CustomInput
            placeholder={t('waste-handover-plan.delivery-number')}
            size="small"
          />
        );
      },
      formItemProps: () => ({

        style: { marginTop: 0, marginBottom: 0 },
      }),
    },
    // ghi chú
    {
      title: t('waste-handover-plan.note'),
      dataIndex: 'note',
      key: 'note',
      width: 200,
      renderFormItem() {
        return (
          <CustomInput placeholder={t('waste-handover-plan.note')} size="small" />
        );
      },
      formItemProps: () => ({
        style: { marginTop: 0, marginBottom: 0 },
      }),
    },
    {
      title: '',
      width: 60,
      fixed: 'right',
      renderFormItem(_, config, form) {
        return (
          <div
            className="cursor-pointer flex justify-center items-center"
            onClick={() => {
              const data = form.getFieldValue(
                'wasteHandoverItems'
              ) as WasteHandoverItem[];
              const listFilter = (data || []).filter(
                (item) =>
                  item.wasteHandoverItemId !== config?.record?.wasteHandoverItemId
              );
              const nextItems =
                listFilter.length > 0
                  ? listFilter
                  : [{ wasteHandoverItemId: nanoid(), stt: 1 } as WasteHandoverItem];

              const normalizedNextItems = nextItems.map((item, index) => ({
                ...item,
                wasteHandoverItemId: item?.wasteHandoverItemId || nanoid(),
                stt: index + 1,
              }));

              form.setFieldValue('wasteHandoverItems', normalizedNextItems);
            }}
          >
            <Icon.Trash className="!fill-[#FC5C5E]" width="18" height="18" />
          </div>
        );
      },
    },
  ];

  return (
    <div className="bg-white p-4 rounded-lg">
      <h3 className="text-base font-semibold mb-4">{t('waste-handover-plan-register.goods-info')}</h3>
      <EditableProTable<WasteHandoverItem>
        rowKey="wasteHandoverItemId"
        name="wasteHandoverItems"
        className={customTableClassName}
        columns={defaultDataTable}
        recordCreatorProps={{
          newRecordType: 'dataSource',
          record: () => {
            const currentItems = (form.getFieldValue('wasteHandoverItems') || []) as WasteHandoverItem[];
            return {
              wasteHandoverItemId: nanoid(),
              stt: (Array.isArray(currentItems) ? currentItems.length : 0) + 1,
            };
          },
          creatorButtonText: t('waste-handover-plan.add-item'),
        }}
        scroll={{ x: 1500 }}
        tableLayout="fixed"
        value={dataSource}
        editable={{
          type: 'multiple',
          editableKeys: computedEditableKeys,
          actionRender: () => {
            return [];
          },
          onChange: setEditableRowKeys,
          deletePopconfirmMessage: t('message.question-delete'),
        }}
      />
    </div>
  );
};

export default WasteHandoverTable;

